import { useMemo } from 'react';
import type { Planting } from '../../../../../models/Planting';
import type { LockedLocationIds, LockedLocationInfo } from '../types';
import { normalizeId } from '../utils';

/**
 * Determines locked location when opening the form from a bed page
 * (single default location with a known bed).
 */
export const useLockedLocation = (
  defaultLocations?: Planting['locations'],
): {
  lockedLocation: LockedLocationIds | null;
  lockedLocationInfo: LockedLocationInfo | null;
} => {
  const lockedLocation = useMemo<LockedLocationIds | null>(() => {
    if (!defaultLocations || defaultLocations.length !== 1) return null;
    const location = defaultLocations[0];
    const bedId = location.bed?.id ?? location.bedId ?? null;
    const plotId = location.plot?.id ?? location.bed?.plot_id ?? null;
    const fieldId = location.field?.id ?? location.plot?.field_id ?? null;
    if (!bedId) return null;
    return {
      fieldId: normalizeId(fieldId),
      plotId: normalizeId(plotId),
      bedId: normalizeId(bedId),
    };
  }, [defaultLocations]);

  const lockedLocationInfo = useMemo<LockedLocationInfo | null>(() => {
    if (!lockedLocation || !defaultLocations || defaultLocations.length === 0) return null;
    const location = defaultLocations[0];
    const fieldLabel = location.field?.name
      ? location.field.name
      : lockedLocation.fieldId != null
        ? `Поле #${lockedLocation.fieldId}`
        : 'Поле не определено';
    const plotLabel = location.plot?.name
      ? location.plot.name
      : lockedLocation.plotId != null
        ? `Участок #${lockedLocation.plotId}`
        : 'Участок не определён';
    const bedLabel = location.bed?.anchor_code
      ? `Грядка ${location.bed.anchor_code}`
      : lockedLocation.bedId != null
        ? `Грядка #${lockedLocation.bedId}`
        : 'Грядка не определена';
    return { fieldLabel, plotLabel, bedLabel };
  }, [defaultLocations, lockedLocation]);

  return { lockedLocation, lockedLocationInfo };
};
